import { BoloBanner } from './Bolo.tsx';
import { AddressFlagsBanner } from './People.tsx';
import { useEffect, useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { fmtDateTime } from '../shared/format.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { DirectorySelect } from '../shared/DirectorySelect.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad, FileDrop } from '../shared/ui.tsx';

const pct = (c: number | null | undefined) => (c == null ? '—' : `${Math.round(c * 100)}%`);

function ConfBadge({ c, threshold }: { c: number; threshold: number }) {
  return <Badge tone={c === 0 ? 'bad' : c < threshold ? 'warn' : 'ok'}>{pct(c)}</Badge>;
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral'> = {
  processing: 'info', ready: 'warn', completed: 'ok', failed: 'bad', pending: 'warn', approved: 'ok', auto_accepted: 'ok', rejected: 'neutral',
};

// ─── Upload + batch list ─────────────────────────────────────────────────────
export function ScanAIHome() {
  const list = useLoad(() => api.get('/api/scanai/batches'));
  const { navigate } = useRouter();
  const [file, setFile] = useState<File | null>(null);
  const a = useAction();
  const processing = list.data?.batches.some((b: any) => b.status === 'processing');
  useEffect(() => {
    if (!processing) return;
    const t = setTimeout(list.reload, 1500);
    return () => clearTimeout(t);
  }, [processing, list.data]);

  return (
    <div className="stack">
      <div className="row between wrap gap">
        <h1>ScanAI intake</h1>
        <Link to="/scanai/learning" className="btn">
          Reader learning
        </Link>
      </div>
      <p className="muted">
        Scan a whole stack of court papers into one PDF. ScanAI splits it into individual papers, reads the fields, and creates one draft per defendant. Every draft is reviewed by a clerk before it becomes a paper.
      </p>
      <Card title="Upload a scanned batch">
        <form
          className="stack sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (!file) return;
            a.run(async () => {
              const r = await api.post('/api/scanai/batches', { filename: file.name, dataBase64: await fileToBase64(file) });
              navigate(`/scanai/batches/${r.batchId}`);
            });
          }}
        >
          <FileDrop accept="application/pdf" file={file} onFile={setFile} label="Scanned batch PDF" hint="One PDF with the whole stack. Up to a few hundred pages." />
          <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={!file || a.busy}>
            {a.busy ? 'Uploading…' : 'Upload and process'}
          </button>
        </form>
        <p className="small muted">
          No scanner handy? <a href="/api/scanai/sample.pdf">Download a fictional sample batch</a> (6 pages, 4 papers, 5 defendants) and upload it.
        </p>
        <ErrorBox error={a.error} />
      </Card>
      <Card title="Batches" actions={<button className="btn sm" onClick={list.reload}>Refresh</button>}>
        {list.loading && !list.data && <Spinner />}
        <ErrorBox error={list.error} />
        {list.data?.batches.length === 0 && <Empty>No batches yet.</Empty>}
        {!!list.data?.batches.length && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>File</th><th>Uploaded</th><th>Pages</th><th>Status</th><th>Drafts</th><th>To review</th><th>Approved</th><th>Rejected</th></tr>
              </thead>
              <tbody>
                {list.data.batches.map((b: any) => (
                  <tr key={b.id} className="clickable" onClick={() => navigate(`/scanai/batches/${b.id}`)}>
                    <td>{b.filename}</td>
                    <td>{fmtDateTime(b.created_at)} <span className="muted small">· {b.uploaded_by}</span></td>
                    <td>{b.page_count}</td>
                    <td><Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{b.status}</Badge></td>
                    <td>{b.drafts}</td>
                    <td>{b.pending}{b.clean_pending ? <span className="muted small"> ({b.clean_pending} clean)</span> : null}</td>
                    <td>{b.approved}</td>
                    <td>{b.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Batch detail ────────────────────────────────────────────────────────────
export function ScanAIBatch({ id }: { id: string }) {
  const r = useLoad(() => api.get(`/api/scanai/batches/${id}`), [id]);
  const meta = useLoad(() => api.get('/api/meta'));
  const typeName = (code: string) => meta.data?.paperTypes.find((t: any) => t.code === code)?.name ?? code.replace(/_/g, ' ').toLowerCase();
  const { navigate } = useRouter();
  const a = useAction();
  const [result, setResult] = useState<any>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  useEffect(() => setSel(new Set()), [r.data]);
  useEffect(() => {
    if (r.data?.batch.status !== 'processing') return;
    const t = setTimeout(r.reload, 1200);
    return () => clearTimeout(t);
  }, [r.data]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const all: any[] = r.data.drafts;
  const pendingIds = all.filter((x) => x.status === 'pending').map((x) => x.id);
  const chosen = all.filter((x) => sel.has(x.id));
  const mergeSel = () =>
    window.confirm(
      `Merge ${chosen.length} drafts into one paper?\n\nIt keeps the person to serve from the first one (${chosen.sort((x, y) => x.page_start - y.page_start || x.defendant_index - y.defendant_index)[0]?.defendant?.value ?? 'first draft'}) and covers pages ${Math.min(...chosen.map((x) => x.page_start))}–${Math.max(...chosen.map((x) => x.page_end))}.`,
    ) &&
    a.run(async () => {
      const m = await api.post(`/api/scanai/batches/${id}/merge`, { draftIds: [...sel] });
      navigate(`/scanai/drafts/${m.draftId}`);
    });
  const deleteSel = () => {
    const reason = window.prompt(`Delete ${sel.size} draft${sel.size === 1 ? '' : 's'}? They won't become papers.\n\nReason (optional):`, 'Not a paper — misread from the scan');
    if (reason === null) return;
    a.run(async () => {
      await api.post(`/api/scanai/batches/${id}/remove`, { draftIds: [...sel], reason: reason.trim() || undefined });
      r.reload();
    });
  };
  const pkgPending = (idx: number) => all.filter((x) => x.package_index === idx).every((x) => x.status === 'pending');
  const firsts = all.filter((x) => x.defendant_index === 0).sort((x, y) => x.page_start - y.page_start);
  const prevPending = (d: any) => {
    const i = firsts.findIndex((x) => x.id === d.id);
    return i > 0 && pkgPending(firsts[i - 1].package_index);
  };
  const repack = (action: 'split' | 'join', page: number) =>
    a.run(async () => {
      await api.post(`/api/scanai/batches/${id}/repackage`, { action, page });
      r.reload();
    });
  const split = (d: any) => {
    const ans = window.prompt(`Pages ${d.page_start}–${d.page_end} hold more than one paper.\nOn which page does the next paper start? (${d.page_start + 1}–${d.page_end})`, String(d.page_start + 1));
    const n = Number(ans);
    if (ans && Number.isInteger(n) && n > d.page_start && n <= d.page_end) repack('split', n);
  };
  const join = (d: any) => window.confirm(`Join pages ${d.page_start}–${d.page_end} with the paper above it?`) && repack('join', d.page_start);
  const { batch, drafts, threshold } = r.data;
  const clean = drafts.filter((d: any) => d.status === 'pending' && d.low_fields.length === 0 && d.duplicates.length === 0);
  const firstPending = drafts.find((d: any) => d.status === 'pending');
  return (
    <div className="stack">
      <div className="row between wrap gap">
        <div>
          <Link to="/scanai">← All batches</Link>
          <h1>{batch.filename}</h1>
          <div className="muted small">
            {batch.page_count} pages · uploaded {fmtDateTime(batch.created_at)} by {batch.uploaded_by} · reader: {batch.provider ?? '…'} · <Badge tone={STATUS_TONE[batch.status] ?? 'neutral'}>{batch.status}</Badge>
          </div>
        </div>
        <div className="row gap">
          <a className="btn sm" href={`/api/documents/${batch.document_id}/content`} target="_blank" rel="noreferrer">View original scan</a>
        </div>
      </div>
      {batch.status === 'processing' && <Notice><Spinner /> Reading the scan… this page refreshes automatically.</Notice>}
      {batch.status === 'failed' && (
        <Notice tone="bad">
          Processing failed: {batch.error}{' '}
          <button className="btn sm" onClick={() => a.run(async () => { await api.post(`/api/scanai/batches/${id}/retry`); r.reload(); })}>Retry</button>
        </Notice>
      )}
      {batch.source === 'portal' && (
        <Notice tone="info">
          Sent by <strong>{batch.sender_name}</strong> through the portal{batch.sender_note ? <> — “{batch.sender_note}”</> : ''}.
          {batch.sender_hints ? ' They filled in the details form; those fields are marked “entered by the sender”.' : ''}
        </Notice>
      )}
      {(batch.notes ?? []).map((n: string, i: number) => <Notice key={i} tone="warn">{n}</Notice>)}
      {batch.status !== 'processing' && firsts.length > 0 && (
        <p className="small muted">
          {batch.page_count} page{batch.page_count === 1 ? '' : 's'} → <strong>{firsts.length} paper{firsts.length === 1 ? '' : 's'}</strong>.
          {batch.can_repackage ? ' Papers stuck together or cut in half? Use Split… or Join ↑ under Pages — the reader learns from it.' : ''}
        </p>
      )}
      {batch.status !== 'processing' && (
        <div className="row gap wrap">
          {firstPending && <button className="btn primary" onClick={() => navigate(`/scanai/drafts/${firstPending.id}`)}>Start review</button>}
          {clean.length > 0 && (
            <button
              className="btn"
              disabled={a.busy}
              onClick={() => window.confirm(`Approve ${clean.length} draft(s) with no flagged fields and no duplicate warnings?`) && a.run(async () => { setResult(await api.post(`/api/scanai/batches/${id}/approve-clean`)); r.reload(); })}
            >
              Approve {clean.length} clean draft{clean.length === 1 ? '' : 's'}
            </button>
          )}
          <span className="muted small">Fields below {pct(threshold)} confidence are flagged for review.</span>
        </div>
      )}
      <ErrorBox error={a.error} />
      {result && (
        <Notice tone="ok">
          Approved {result.approved.length}: {result.approved.map((x: any) => x.civilosNumber + (x.bolo ? ' (BOLO — marked urgent)' : '')).join(', ') || 'none'}.
          {result.skipped.length > 0 && ` ${result.skipped.length} left for individual review.`}
        </Notice>
      )}
      {drafts.length > 0 && (
        <Card title={`Drafts (${drafts.length})`}>
          {sel.size > 0 && (
            <div className="bulk-bar" role="toolbar" aria-label="Selected drafts">
              <strong>{sel.size} selected</strong>
              <button type="button" className="btn sm" disabled={a.busy || sel.size < 2} onClick={mergeSel} title="These drafts are really one paper">
                Merge into one
              </button>
              <button type="button" className="btn sm danger" disabled={a.busy} onClick={deleteSel} title="These aren't papers (misread lines, blank or duplicate pages)">
                Delete
              </button>
              <button type="button" className="btn sm ghost" onClick={() => setSel(new Set())}>
                Clear selection
              </button>
            </div>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    {pendingIds.length > 0 && (
                      <input type="checkbox" aria-label="Select all waiting drafts" checked={sel.size > 0 && sel.size === pendingIds.length} onChange={(e) => setSel(e.target.checked ? new Set(pendingIds) : new Set())} />
                    )}
                  </th>
                  <th>Pages</th><th>Paper type</th><th>Case #</th><th>Defendant</th><th>Address</th><th>Lowest confidence</th><th>Flags</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {drafts.map((d: any) => (
                  <tr key={d.id} className={sel.has(d.id) ? 'sel' : ''}>
                    <td>
                      {d.status === 'pending' && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${d.defendant?.value ?? 'draft'} (pages ${d.page_start}–${d.page_end})`}
                          checked={sel.has(d.id)}
                          onChange={(e) => {
                            const n = new Set(sel);
                            if (e.target.checked) n.add(d.id);
                            else n.delete(d.id);
                            setSel(n);
                          }}
                        />
                      )}
                    </td>
                    <td>
                      {d.page_start === d.page_end ? d.page_start : `${d.page_start}–${d.page_end}`}
                      {batch.can_repackage && d.defendant_index === 0 && pkgPending(d.package_index) && (
                        <div className="pkg-tools">
                          {d.page_end > d.page_start && (
                            <button type="button" className="btn sm ghost" disabled={a.busy} title="These pages hold more than one paper" onClick={() => split(d)}>
                              Split…
                            </button>
                          )}
                          {prevPending(d) && (
                            <button type="button" className="btn sm ghost" disabled={a.busy} title="This paper belongs with the one above it" onClick={() => join(d)}>
                              Join ↑
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{d.paper_type?.value ? typeName(d.paper_type.value) : <span className="text-warn">missing</span>}</td>
                    <td className="mono">{d.case_number?.value ?? <span className="text-warn">missing</span>}</td>
                    <td>{d.defendant?.value ?? <span className="text-warn">missing</span>}</td>
                    <td className="muted">{d.address?.value ?? <span className="text-warn">missing</span>}</td>
                    <td><ConfBadge c={d.min_confidence} threshold={threshold} /></td>
                    <td className="small">
                      {d.low_fields.length > 0 && <div>{d.low_fields.length} field{d.low_fields.length === 1 ? '' : 's'} to check</div>}
                      {d.duplicates.length > 0 && <Badge tone="bad">possible duplicate</Badge>}
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[d.status] ?? 'neutral'}>{d.status.replace('_', ' ')}</Badge>
                      {d.civilos_number && <div><Link to={`/papers/${d.paper_id}`} className="mono small">{d.civilos_number}</Link></div>}
                    </td>
                    <td>{d.status === 'pending' && <Link to={`/scanai/drafts/${d.id}`} className="btn sm">Review</Link>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {batch.status === 'ready' && drafts.length === 0 && <Empty>No papers were detected in this scan. Enter them with New intake instead.</Empty>}
    </div>
  );
}

// ─── Page strip: see every page of this paper, split or join right here ─────
function PageStrip({ batchId, start, end, repack, current, onView, onDone }: { batchId: string; start: number; end: number; repack: any; current: number; onView: (n: number) => void; onDone: (drafts: any[]) => void }) {
  const a = useAction();
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  const go = (action: 'split' | 'join', page: number) =>
    a.run(async () => {
      const r = await api.post(`/api/scanai/batches/${batchId}/repackage`, { action, page });
      onDone(r.drafts ?? []);
    });
  return (
    <div className="page-strip">
      <div className="page-strip-h">
        <strong>
          {pages.length === 1 ? `Page ${start}` : `Pages ${start}–${end}`} · {pages.length} page{pages.length === 1 ? '' : 's'} in this paper
        </strong>
        {repack?.canJoinPrevious && (
          <button
            type="button"
            className="btn sm"
            disabled={a.busy}
            onClick={() => window.confirm(`Put these pages with the paper before it (pages ${repack.previousPages[0]}–${repack.previousPages[1]})?`) && go('join', start)}
            title="This isn't a separate paper — it belongs with the one before"
          >
            ⤒ Join with the paper before
          </button>
        )}
      </div>
      <div className="page-strip-row" role="list">
        {pages.map((n, i) => (
          <div key={n} className="page-strip-cell" role="listitem">
            {i > 0 && repack?.can && (
              <button
                type="button"
                className="page-cut"
                disabled={a.busy}
                onClick={() => window.confirm(`Start a new paper at page ${n}? Pages ${start}–${n - 1} stay here; ${n}–${end} become a separate paper.`) && go('split', n)}
                title={`A new paper starts on page ${n}`}
                aria-label={`Split: a new paper starts on page ${n}`}
              >
                <span aria-hidden>✂</span>
                <small>New paper starts here</small>
              </button>
            )}
            <button type="button" className={`page-thumb ${current === n ? 'on' : ''}`} onClick={() => onView(n)} aria-label={`Show page ${n}`} aria-pressed={current === n}>
              {broken[n] ? (
                <span className="page-thumb-ph">Page {n}</span>
              ) : (
                <img src={`/api/scanai/batches/${batchId}/pages/${n}?w=240`} alt={`Page ${n}`} loading="lazy" onError={() => setBroken((b) => ({ ...b, [n]: true }))} />
              )}
              <span className="page-thumb-n">{n}</span>
            </button>
          </div>
        ))}
      </div>
      {!repack?.can && pages.length > 1 && <p className="small muted">This scan was read before page splitting was added — upload it again to split it here.</p>}
      <ErrorBox error={a.error} />
    </div>
  );
}

/** "+ Phone": no box until someone has a number to add. */
function PhoneAdd({ label, value, onChange }: { label: string; value: string | null; onChange: (v: string | null) => void }) {
  if (value === null)
    return (
      <button type="button" className="btn sm ghost phone-add" onClick={() => onChange('')}>
        + Phone
      </button>
    );
  return (
    <div className="phone-row">
      <input type="tel" aria-label={label} placeholder="(803) 555-0100" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={value === ''} />
      <button type="button" className="btn sm ghost" aria-label={`Remove ${label.toLowerCase()}`} onClick={() => onChange(null)}>
        ✕
      </button>
    </div>
  );
}

// ─── Draft review (side by side) ─────────────────────────────────────────────
type Values = {
  paperTypeCode: string; caseNumber: string; plaintiff: string; plaintiffPhone: string | null; courtId: string; customerId: string; courtDate: string; courtTime: string; issueDate: string; specialInstructions: string; ivD: boolean;
  defendant: { name: string; phone: string | null; address: { line1: string; line2: string; city: string; state: string; zip: string } };
};

const blankIfNull = (v: any): Values => ({
  paperTypeCode: v.paperTypeCode ?? '', caseNumber: v.caseNumber ?? '', plaintiff: v.plaintiff ?? '', plaintiffPhone: v.plaintiffPhone ?? null, courtId: v.courtId ?? '', customerId: v.customerId ?? '',
  courtDate: v.courtDate ?? '', courtTime: v.courtTime ?? '', issueDate: v.issueDate ?? '', specialInstructions: v.specialInstructions ?? '', ivD: v.ivD === true,
  defendant: { name: v.defendant.name ?? '', phone: v.defendant.phone ?? null, address: { line1: v.defendant.address.line1 ?? '', line2: v.defendant.address.line2 ?? '', city: v.defendant.address.city ?? '', state: v.defendant.address.state ?? '', zip: v.defendant.address.zip ?? '' } },
});

export function ScanAIDraft({ id }: { id: string }) {
  const r = useLoad(() => api.get(`/api/scanai/drafts/${id}`), [id]);
  const meta = useLoad(() => api.get('/api/meta'));
  const { navigate } = useRouter();
  const [v, setV] = useState<Values | null>(null);
  const [override, setOverride] = useState(false);
  const [page, setViewPage] = useState(0);
  const [boloHits, setBoloHits] = useState<{ paperId: string; civilosNumber: string; bolos: any[]; flags: any[] } | null>(null);
  const a = useAction();
  useEffect(() => {
    if (r.data?.values) setV(blankIfNull(r.data.values));
    setOverride(false);
    setViewPage(0);
  }, [r.data]);
  if (!r.data || !meta.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { draft, batch, labels, threshold, order } = r.data;
  const idx = order.findIndex((o: any) => o.id === id);
  const nextPending = [...order.slice(idx + 1), ...order.slice(0, idx)].find((o: any) => o.status === 'pending' && o.id !== id);
  const goNext = () => navigate(nextPending ? `/scanai/drafts/${nextPending.id}` : `/scanai/batches/${batch.id}`);

  if (draft.status !== 'pending') {
    return (
      <div className="stack">
        <Link to={`/scanai/batches/${batch.id}`}>← Back to batch</Link>
        <Notice tone="ok">This draft was {draft.status.replace('_', ' ')}{draft.reject_reason ? `: ${draft.reject_reason}` : ''}.</Notice>
        {draft.paper_id && <Link to={`/papers/${draft.paper_id}`} className="btn">Open paper</Link>}
      </div>
    );
  }
  if (!v) return <Spinner />;
  const viewPage = page || draft.page_start;

  const f = (key: string) => draft.fields[key] ?? { value: null, confidence: 0 };
  const flagged = (key: string) => draft.low_fields.includes(key);
  const Conf = ({ k }: { k: string }) => (f(k).value === null ? <Badge tone={flagged(k) ? 'bad' : 'neutral'}>not found</Badge> : <ConfBadge c={f(k).confidence} threshold={threshold} />);
  const cls = (k: string) => (flagged(k) ? 'scan-field flagged' : 'scan-field');
  const set = (k: keyof Values) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const setA = (k: keyof Values['defendant']['address']) => (e: { target: { value: string } }) => setV({ ...v, defendant: { ...v.defendant, address: { ...v.defendant.address, [k]: e.target.value } } });
  const L = (k: string) => (
    <span className="row gap-sm">
      {labels[k] ?? k} <Conf k={k} />
      {flagged(k) && <span className="text-warn small">check</span>}
      {(f(k) as any).source === 'learned' || (f(k) as any).source === 'both' ? (
        <span className="learn-tag" title={(f(k) as any).basis ?? ''}>
          learned · {(f(k) as any).basis}
        </span>
      ) : (f(k) as any).basis ? (
        <span className="small muted" title={(f(k) as any).basis}>
          {(f(k) as any).basis}
        </span>
      ) : null}
    </span>
  );

  const approve = () =>
    a.run(async () => {
      const nul = (s: string) => (s.trim() ? s.trim() : null);
      const res = await api.post(`/api/scanai/drafts/${id}/approve`, {
        overrideDuplicates: override,
        values: {
          paperTypeCode: nul(v.paperTypeCode), caseNumber: nul(v.caseNumber), plaintiff: nul(v.plaintiff), plaintiffPhone: v.plaintiffPhone ? nul(v.plaintiffPhone) : null, courtId: nul(v.courtId), customerId: nul(v.customerId),
          courtDate: nul(v.courtDate), courtTime: nul(v.courtTime), issueDate: nul(v.issueDate), specialInstructions: nul(v.specialInstructions), ivD: v.ivD,
          defendant: { name: nul(v.defendant.name), phone: v.defendant.phone ? nul(v.defendant.phone) : null, address: Object.fromEntries(Object.entries(v.defendant.address).map(([k, x]) => [k, nul(x)])) },
        },
      });
      if (res.bolos?.length || res.addressFlags?.length) setBoloHits({ paperId: res.paperId, civilosNumber: res.civilosNumber, bolos: res.bolos ?? [], flags: res.addressFlags ?? [] });
      else goNext();
    });

  if (boloHits)
    return (
      <div className="stack">
        <h1>Approved {boloHits.civilosNumber}</h1>
        <BoloBanner hits={boloHits.bolos} />
        <AddressFlagsBanner flags={boloHits.flags} />
        <div className="row gap">
          <Link to={`/papers/${boloHits.paperId}`} className="btn primary">Open the paper</Link>
          <button className="btn" onClick={() => (setBoloHits(null), goNext())}>Next draft</button>
        </div>
      </div>
    );

  return (
    <div className="stack">
      <div className="row between wrap gap">
        <div>
          <Link to={`/scanai/batches/${batch.id}`}>← {batch.filename}</Link>
          <h1>Review draft {idx + 1} of {order.length}</h1>
          <div className="muted small">
            Pages {draft.page_start}–{draft.page_end} · defendant {draft.defendant_index + 1} of this package · fields below {pct(threshold)} are highlighted
          </div>
        </div>
        {nextPending && <button className="btn ghost" onClick={goNext}>Skip for now</button>}
      </div>
      {draft.notes.map((n: string, i: number) => <Notice key={i} tone="warn">{n}</Notice>)}
      {draft.duplicates.length > 0 && (
        <Notice tone="bad">
          Possible duplicate of open paper(s):{' '}
          {draft.duplicates.map((d: any) => <span key={d.id}><Link to={`/papers/${d.id}`}>{d.civilos_number}</Link> ({d.reason}) </span>)}
          <label className="row gap-sm"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> I checked — create it anyway</label>
        </Notice>
      )}
      <div className="scan-review">
        <div className="scan-doc">
          <PageStrip
            batchId={batch.id}
            start={draft.page_start}
            end={draft.page_end}
            repack={r.data.repack}
            current={viewPage}
            onView={setViewPage}
            onDone={(drafts: any[]) => {
              // Go to the rebuilt paper that holds the page the clerk was on.
              const at = drafts.find((x) => x.page_start <= draft.page_start && draft.page_start <= x.page_end && x.defendant_index === 0) ?? drafts[0];
              if (at) navigate(`/scanai/drafts/${at.id}`);
            }}
          />
          <iframe key={viewPage} title="Scanned pages" src={`/api/documents/${draft.package_document_id}/content#page=${viewPage - draft.page_start + 1}`} />
        </div>
        <form className="stack" data-unsaved="true" onSubmit={(e) => { e.preventDefault(); approve(); }}>
          <Card title="Paper">
            <div className="grid2">
              <div className={cls('paperTypeCode')}>
                <Field label="Paper type *">
                  <select value={v.paperTypeCode} onChange={set('paperTypeCode')} required>
                    <option value="">Select…</option>
                    {meta.data.paperTypes.map((t: any) => <option key={t.code} value={t.code}>{t.name}</option>)}
                  </select>
                </Field>
                <div className="conf-row">{L('paperTypeCode')}</div>
              </div>
              <div className={cls('caseNumber')}>
                <Field label="Case number *"><input value={v.caseNumber} onChange={set('caseNumber')} required /></Field>
                <div className="conf-row">{L('caseNumber')}</div>
              </div>
              <div className={cls('plaintiff')}>
                <Field label="Plaintiff / petitioner"><input value={v.plaintiff} onChange={set('plaintiff')} /></Field>
                <PhoneAdd label="Plaintiff phone" value={v.plaintiffPhone} onChange={(x) => setV({ ...v, plaintiffPhone: x })} />
                <div className="conf-row">{L('plaintiff')}</div>
              </div>
              <div className={cls('court')}>
                <Field label="Court" hint={f('court').value ? `Read as: “${f('court').value}”${draft.suggestions.courtMatch ? ` → matched ${draft.suggestions.courtMatch}` : ''}` : undefined}>
                  <DirectorySelect kind="court" value={v.courtId} options={meta.data.courts} suggestName={f('court').value} onChange={(x) => setV({ ...v, courtId: x })} onAdded={async (newId) => (await meta.reload(), setV({ ...v, courtId: newId }))} />
                </Field>
                <div className="conf-row">{L('court')}</div>
              </div>
              <div className={cls('customerName')}>
                <Field label="Customer" hint={f('customerName').value ? `Read as: “${f('customerName').value}”${draft.suggestions.customerMatch ? ` → matched ${draft.suggestions.customerMatch}` : ' (no matching customer)'}` : undefined}>
                  <DirectorySelect kind="customer" value={v.customerId} options={meta.data.customers} suggestName={f('customerName').value} onChange={(x) => setV({ ...v, customerId: x })} onAdded={async (newId) => (await meta.reload(), setV({ ...v, customerId: newId }))} />
                </Field>
                <div className="conf-row">{L('customerName')}</div>
              </div>
              <div className={cls('issueDate')}>
                <Field label="Issue date"><input type="date" value={v.issueDate} onChange={set('issueDate')} /></Field>
                <div className="conf-row">{L('issueDate')}</div>
              </div>
              <div className={cls('courtDate')}>
                <Field label="Court / return date"><input type="date" value={v.courtDate} onChange={set('courtDate')} /></Field>
                <div className="conf-row">{L('courtDate')}</div>
              </div>
              <div className={cls('courtTime')}>
                <Field label="Court time"><input type="time" value={v.courtTime} onChange={set('courtTime')} /></Field>
                <div className="conf-row">{L('courtTime')}</div>
              </div>
            </div>
            <div className={cls('specialInstructions')}>
              <Field label="Special instructions"><textarea rows={2} value={v.specialInstructions} onChange={set('specialInstructions')} /></Field>
              <div className="conf-row">{L('specialInstructions')}</div>
            </div>
            <label className="row gap-sm ivd-check">
              <input type="checkbox" checked={v.ivD} onChange={(e) => setV({ ...v, ivD: e.target.checked })} /> IV-D (child support) paper
            </label>
          </Card>
          <Card title="Person to serve / Defendant">
            <div className="grid2">
              <div className={cls('defendant.name')}>
                <Field label="Name *"><input value={v.defendant.name} onChange={(e) => setV({ ...v, defendant: { ...v.defendant, name: e.target.value } })} required /></Field>
                <PhoneAdd label="Defendant phone" value={v.defendant.phone} onChange={(x) => setV({ ...v, defendant: { ...v.defendant, phone: x } })} />
                <div className="conf-row">{L('defendant.name')}</div>
              </div>
              <div className={cls('address.line1')}>
                <Field label="Street address *"><input value={v.defendant.address.line1} onChange={setA('line1')} required /></Field>
                <div className="conf-row">{L('address.line1')}</div>
              </div>
              <div className={cls('address.line2')}>
                <Field label="Unit / line 2"><input value={v.defendant.address.line2} onChange={setA('line2')} /></Field>
                <div className="conf-row">{L('address.line2')}</div>
              </div>
              <div className={cls('address.city')}>
                <Field label="City"><input value={v.defendant.address.city} onChange={setA('city')} /></Field>
                <div className="conf-row">{L('address.city')}</div>
              </div>
              <div className="row gap">
                <div className={cls('address.state')}>
                  <Field label="State"><input value={v.defendant.address.state} onChange={setA('state')} maxLength={2} style={{ width: 70 }} /></Field>
                </div>
                <div className={cls('address.zip')}>
                  <Field label="ZIP"><input value={v.defendant.address.zip} onChange={setA('zip')} style={{ width: 110 }} /></Field>
                </div>
              </div>
            </div>
          </Card>
          <ErrorBox error={a.error} />
          <div className="row gap wrap">
            <button className="btn primary" disabled={a.busy || (draft.duplicates.length > 0 && !override)}>
              {a.busy ? 'Saving…' : nextPending ? 'Approve & next' : 'Approve'}
            </button>
            <button
              type="button"
              className="btn warn"
              disabled={a.busy}
              onClick={() => {
                const reason = window.prompt('Reason for rejecting this draft (kept in the audit log)');
                if (reason) a.run(async () => { await api.post(`/api/scanai/drafts/${id}/reject`, { reason }); goNext(); });
              }}
            >
              Reject
            </button>
            <span className="small muted">Approving creates the paper in Intake status; the normal review and payment steps still apply.</span>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ScanAISettings({ s, set }: { s: any; set: (k: string) => (e: any) => void }) {
  return (
    <Card title="ScanAI">
      <div className="grid2">
        <Field label="Confidence threshold (%)" hint="Fields below this are highlighted for the clerk. Spec default: 90%.">
          <input type="number" min={50} max={100} value={Math.round((Number(s.scanConfidenceThreshold ?? 0.9)) * 100)} onChange={(e) => set('scanConfidenceThreshold')({ target: { value: Number(e.target.value) / 100, type: 'number' } })} />
        </Field>
        <Field label="Document reader" hint="The built-in reader handles text PDFs, and reads image-only copier scans with OCR on this server (every OCR field is flagged for the clerk to check). The AI reader is more accurate on messy or handwritten scans but sends pages to an external service — requires agency approval and a server API key.">
          <select value={s.scanProvider ?? 'text'} onChange={set('scanProvider')}>
            <option value="text">Built-in reader with OCR (no data leaves the server)</option>
            <option value="anthropic">AI reader (Claude) — scanned images</option>
          </select>
        </Field>
      </div>
      <label className="row gap-sm">
        <input type="checkbox" checked={!!s.scanAutoAccept} onChange={set('scanAutoAccept')} /> Auto-accept drafts where every field meets the threshold and no duplicate is found (default off — clerk reviews every draft)
      </label>
    </Card>
  );
}

/** How well the reader is doing on this agency's papers, and what it has learned from approvals. */
export function ScanAILearning() {
  const r = useLoad(() => api.get<any>('/api/scanai/learning'));
  const a = useAction();
  const toggle = (id: string, disabled: boolean) => a.run(async () => (await api.post(`/api/scanai/learning/rules/${id}/${disabled ? 'enable' : 'disable'}`), r.reload()));
  const pctText = (n: number | null) => (n == null ? '—' : `${n}%`);
  return (
    <div className="stack">
      <div className="row between wrap gap">
        <h1>Reader learning</h1>
        <Link to="/scanai" className="btn">
          ← ScanAI intake
        </Link>
      </div>
      <p className="muted">
        Every paper a clerk approves teaches the reader where values sit on your court papers — the label before a case number, the line under “Serve at”, the usual case-number pattern. Each clue keeps a record of how
        often it was right, and confidence comes from that record, so new clues start out flagged for review and earn trust. Nothing leaves this server.
      </p>
      <ErrorBox error={r.error ?? a.error} />
      {!r.data ? (
        <Spinner />
      ) : r.data.papersLearnedFrom === 0 ? (
        <Empty>Nothing learned yet. Approve a few scanned papers and the reader starts learning from them.</Empty>
      ) : (
        <Card title={`Learned from ${r.data.papersLearnedFrom} approved paper${r.data.papersLearnedFrom === 1 ? '' : 's'}`}>
          <div className="table-wrap">
            <table className="table learn-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th className="num">Papers</th>
                  <th>Read right · first → latest</th>
                  <th className="num">OCR scans</th>
                  <th>Clues learned (right / used)</th>
                </tr>
              </thead>
              <tbody>
                {r.data.fields
                  .filter((f: any) => f.papers > 0 || f.clueCount > 0)
                  .map((f: any) => (
                    <tr key={f.field}>
                      <td>
                        <strong>{f.label}</strong>
                      </td>
                      <td className="num">{f.papers}</td>
                      <td>
                        <span className="learn-trend">
                          <span className="muted">{pctText(f.earlyPct)}</span> → <strong>{pctText(f.recentPct)}</strong>
                        </span>
                        <span className="learn-bar" aria-hidden>
                          <span style={{ width: `${f.recentPct ?? 0}%` }} />
                        </span>
                      </td>
                      <td className="num">{f.ocrPapers ? `${pctText(f.ocrPct)} of ${f.ocrPapers}` : '—'}</td>
                      <td>
                        {f.clues.length === 0 ? (
                          <span className="muted small">none yet</span>
                        ) : (
                          <ul className="plain learn-clues">
                            {f.clues.map((c: any) => (
                              <li key={c.id} className={c.disabled ? 'off' : ''}>
                                <span className="grow">
                                  {c.text} <span className="muted small">· {c.hits}/{c.hits + c.misses} · trust {c.trust}%</span>
                                </span>
                                <button className="btn sm ghost" disabled={a.busy} onClick={() => toggle(c.id, c.disabled)}>
                                  {c.disabled ? 'Restore' : 'Forget'}
                                </button>
                              </li>
                            ))}
                            {f.clueCount > f.clues.length && <li className="muted small">+{f.clueCount - f.clues.length} more</li>}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="small muted">“First → latest” compares the reader’s earliest papers with its most recent ones. Forget a clue if it keeps pulling the wrong thing; it stops being used right away.</p>
        </Card>
      )}
    </div>
  );
}
